/**
 * Calendar rules shared by the server and every client (contracts calendar.ts): wall-clock time in an IANA zone,
 * expanding recurring events into occurrences (DST-safe: occurrences keep their local time), the week and month
 * grids, laying out overlapping events side by side in a day column (Google Calendar), and iCalendar (RFC 5545 /
 * iTIP) invites and replies for outside guests.
 */
import type { Attendee, CalendarEvent, EventOccurrence, Member, OccurrenceOverride, Recurrence, RsvpStatus } from "@bundle/contracts";
import { type MessageDescriptor, msg } from "../i18n.ts";

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

/** Most occurrences one expansion returns (a range of a year of a daily event fits). */
export const MAX_OCCURRENCES = 1000;

export type LocalParts = { year: number; month: number; day: number; hour: number; minute: number; second: number; weekday: number };

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (f === undefined) {
    try {
      f = new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric", weekday: "short" });
    } catch {
      f = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric", weekday: "short" });
    }
    formatters.set(timeZone, f);
  }
  return f;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** The wall-clock date and time of an instant in a zone (unknown zones read as UTC). Month is 1–12, weekday 0 = Sunday. */
export function localParts(ts: number, timeZone: string): LocalParts {
  const p: Record<string, string> = {};
  for (const part of formatter(timeZone).formatToParts(new Date(ts))) p[part.type] = part.value;
  return {
    year: Number(p.year), month: Number(p.month), day: Number(p.day), hour: Number(p.hour) % 24, minute: Number(p.minute), second: Number(p.second),
    weekday: WEEKDAYS[p.weekday ?? "Sun"] ?? 0,
  };
}

/** The instant of a wall-clock time in a zone. A time skipped by DST moves forward; a repeated one takes the first. */
export function fromLocal(year: number, month: number, day: number, hour: number, minute: number, timeZone: string): number {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  let ts = guess;
  for (let i = 0; i < 3; i++) {
    const p = localParts(ts, timeZone);
    const shown = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    const diff = guess - shown;
    if (diff === 0) break;
    ts += diff;
  }
  return ts;
}

/** Local midnight of the day containing `ts`. */
export function startOfDay(ts: number, timeZone: string): number {
  const p = localParts(ts, timeZone);
  return fromLocal(p.year, p.month, p.day, 0, 0, timeZone);
}

/** Local midnight `n` days after the day containing `ts` (calendar days, not 24-hour steps). */
export function addDays(ts: number, n: number, timeZone: string): number {
  const p = localParts(ts, timeZone);
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + n));
  return fromLocal(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), 0, 0, timeZone);
}

/** Local midnight starting the week that contains `ts` (`firstDay`: 0 = Sunday, 1 = Monday). */
export function startOfWeek(ts: number, timeZone: string, firstDay: number): number {
  const p = localParts(ts, timeZone);
  return addDays(ts, -((p.weekday - firstDay + 7) % 7), timeZone);
}

/** The 7 day starts of the week containing `ts`. */
export function weekDays(ts: number, timeZone: string, firstDay: number): number[] {
  const start = startOfWeek(ts, timeZone, firstDay);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i, timeZone));
}

/** The month grid around `ts`: 6 weeks × 7 day starts, beginning on the week that holds the 1st. */
export function monthGrid(ts: number, timeZone: string, firstDay: number): number[] {
  const p = localParts(ts, timeZone);
  const first = fromLocal(p.year, p.month, 1, 0, 0, timeZone);
  const start = startOfWeek(first, timeZone, firstDay);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i, timeZone));
}

/** Same local day? */
export function sameDay(a: number, b: number, timeZone: string): boolean {
  const x = localParts(a, timeZone);
  const y = localParts(b, timeZone);
  return x.year === y.year && x.month === y.month && x.day === y.day;
}

// ---------------------------------------------------------------------------------------------------------------
// Recurrence

type Expandable = Pick<CalendarEvent, "start" | "end" | "timeZone"> & { recurrence?: Recurrence | undefined };

/** An answer to one occurrence (original start `at`), or with `following` to it and every later one. */
export type OccurrenceResponse = { at: number; response: RsvpStatus; following?: boolean | undefined };

/**
 * The answer someone gave for the occurrence at `at` (original start) among their per-occurrence answers: one to it
 * alone, else the latest "this and following" answer from it or an earlier occurrence. Undefined when neither (their
 * series answer holds).
 */
export function occurrenceResponse(answers: readonly OccurrenceResponse[] | undefined, at: number): RsvpStatus | undefined {
  let from: OccurrenceResponse | undefined;
  for (const a of answers ?? []) {
    if (a.following !== true) {
      if (a.at === at) return a.response;
    } else if (a.at <= at && (from === undefined || a.at > from.at)) from = a;
  }
  return from?.response;
}

/**
 * Per-occurrence answers after answering the occurrence at `answer.at`: on its own (replacing an earlier answer to it
 * alone), or with `following` from it on ("This and following events": answers to it and later occurrences give way;
 * the organizer's series is left as it is, like Google). Sorted by `at`, at most 500 kept (the latest).
 */
export function withOccurrenceResponse(answers: readonly OccurrenceResponse[] | undefined, answer: OccurrenceResponse): OccurrenceResponse[] {
  const following = answer.following === true;
  const rest = (answers ?? []).filter((a) => (following ? a.at < answer.at : a.at !== answer.at || a.following === true));
  const one: OccurrenceResponse = following ? { at: answer.at, response: answer.response, following: true } : { at: answer.at, response: answer.response };
  return [...rest, one].sort((x, y) => x.at - y.at || Number(x.following === true) - Number(y.following === true)).slice(-500);
}

/**
 * The occurrences to show for an event in [from, to): `expandOccurrences`, then the series' single-occurrence changes
 * (`recurrence.overrides`: a new time can move one in or out of the range) and my answers to single occurrences.
 * Changed or answered ones carry `at`, their original start.
 */
export function occurrencesOf(
  e: Expandable & Pick<CalendarEvent, "id"> & { myOccurrenceResponses?: readonly OccurrenceResponse[] | undefined },
  from: number,
  to: number,
  max: number = MAX_OCCURRENCES,
): EventOccurrence[] {
  const overrides = e.recurrence?.overrides ?? [];
  const answers = e.myOccurrenceResponses ?? [];
  if (overrides.length === 0 && answers.length === 0) return expandOccurrences(e, from, to, max).map((o) => ({ eventId: e.id, start: o.start, end: o.end }));
  const byAt = new Map(overrides.map((o) => [o.at, o]));
  const reach = Math.max(0, ...overrides.map((o) => Math.max(Math.abs(o.start - o.at), o.end - o.start)));
  const out: EventOccurrence[] = [];
  for (const o of expandOccurrences(e, from - reach, to + reach, max * 2)) {
    const ov = byAt.get(o.start);
    const answer = occurrenceResponse(answers, o.start);
    const occ: EventOccurrence = { eventId: e.id, start: ov?.start ?? o.start, end: ov?.end ?? o.end };
    if (ov !== undefined || answer !== undefined) occ.at = o.start;
    if (ov?.title !== undefined) occ.title = ov.title;
    if (ov?.location !== undefined) occ.location = ov.location;
    if (ov?.description !== undefined) occ.description = ov.description;
    if (answer !== undefined) occ.myResponse = answer;
    if (occ.start < to && occ.end > from) out.push(occ);
  }
  return out.sort((a, b) => a.start - b.start).slice(0, max);
}

/**
 * The series as my calendar shows it: occurrences I declined are left out (EXDATE in .ics). Declining "this and
 * following" leaves out every occurrence from there to my next "this and following" answer; when it holds to the end,
 * the series ends before it instead.
 */
export function myRecurrence(e: Expandable & Pick<CalendarEvent, "myOccurrenceResponses">): Recurrence | undefined {
  const answers = e.myOccurrenceResponses ?? [];
  const r = e.recurrence;
  if (r === undefined || !answers.some((a) => a.response === "declined")) return r;
  const declined = answers.filter((a) => a.following !== true && a.response === "declined").map((a) => a.at);
  const rules = answers.filter((a) => a.following === true).sort((a, b) => a.at - b.at);
  const last = rules[rules.length - 1];
  const cut = last?.response === "declined" ? splitRecurrence({ ...e, recurrence: r }, last.at).before : undefined;
  const { exdates: _x, overrides: _o, ...rule } = r;
  void _x;
  void _o;
  rules.forEach((f, i) => {
    if (f.response !== "declined" || (f === last && cut !== undefined)) return;
    const end = rules[i + 1]?.at ?? Number.MAX_SAFE_INTEGER;
    for (const o of expandOccurrences({ ...e, recurrence: rule }, f.at, end, 500)) {
      if (o.start >= f.at && o.start < end && occurrenceResponse(answers, o.start) === "declined") declined.push(o.start);
    }
  });
  const base = cut ?? r;
  const kept = cut === undefined || last === undefined ? declined : declined.filter((x) => x < last.at);
  const exdates = [...new Set([...(base.exdates ?? []), ...kept])].sort((a, b) => a - b).slice(0, 500);
  return exdates.length === 0 ? base : { ...base, exdates };
}

/** The series with one occurrence (original start `at`) changed on its own; an unchanged one drops its override. */
export function withOverride(r: Recurrence, o: OccurrenceOverride, seriesDuration: number): Recurrence {
  const rest = (r.overrides ?? []).filter((x) => x.at !== o.at);
  const same = o.start === o.at && o.end - o.start === seriesDuration && o.title === undefined && o.location === undefined && o.description === undefined;
  const overrides = same ? rest : [...rest, o].sort((a, b) => a.at - b.at);
  const { overrides: _old, ...base } = r;
  void _old;
  return overrides.length === 0 ? base : { ...base, overrides };
}

/**
 * Occurrences of an event overlapping [from, to), at most `max`. A recurring event repeats at its local start time
 * (so a 9:00 meeting stays at 9:00 across DST); `count` counts from the first occurrence, `until` is inclusive,
 * `exdates` are skipped. Weekly with `weekdays` repeats on those days of each `interval`-th week.
 */
export function expandOccurrences(e: Expandable, from: number, to: number, max: number = MAX_OCCURRENCES): { start: number; end: number }[] {
  const duration = Math.max(0, e.end - e.start);
  const r = e.recurrence;
  if (r === undefined) return e.start < to && e.end > from ? [{ start: e.start, end: e.end }] : [];
  const tz = e.timeZone;
  const s = localParts(e.start, tz);
  const ex = new Set(r.exdates ?? []);
  const interval = Math.max(1, r.interval ?? 1);
  const out: { start: number; end: number }[] = [];
  let produced = 0;
  const emit = (start: number): boolean => {
    if (start < e.start) return true;
    if (r.until !== undefined && start > r.until) return false;
    if (r.count !== undefined && produced >= r.count) return false;
    produced++;
    if (start >= to) return false;
    if (!ex.has(start) && start + duration > from) out.push({ start, end: start + duration });
    return out.length < max;
  };
  const at = (y: number, m: number, d: number) => fromLocal(y, m, d, s.hour, s.minute, tz);
  const date = (y: number, m: number, d: number) => {
    const x = new Date(Date.UTC(y, m - 1, d));
    return { y: x.getUTCFullYear(), m: x.getUTCMonth() + 1, d: x.getUTCDate() };
  };
  for (let i = 0; i < 100_000; i++) {
    if (r.freq === "daily") {
      const d = date(s.year, s.month, s.day + i * interval);
      if (!emit(at(d.y, d.m, d.d))) break;
    } else if (r.freq === "weekly") {
      const days = (r.weekdays !== undefined && r.weekdays.length > 0 ? [...new Set(r.weekdays)] : [s.weekday]).sort((a, b) => a - b);
      // Week i starts on the Sunday of the start's week.
      const weekStart = date(s.year, s.month, s.day - s.weekday + i * 7 * interval);
      let more = true;
      for (const wd of days) {
        const d = date(weekStart.y, weekStart.m, weekStart.d + wd);
        if (!(more = emit(at(d.y, d.m, d.d)))) break;
      }
      if (!more) break;
    } else if (r.freq === "monthly") {
      const total = s.month - 1 + i * interval;
      const y = s.year + Math.floor(total / 12);
      const m = (total % 12) + 1;
      const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
      if (s.day > last) continue; // like Google: the 31st skips shorter months
      if (!emit(at(y, m, s.day))) break;
    } else {
      const y = s.year + i * interval;
      if (s.month === 2 && s.day === 29 && new Date(Date.UTC(y, 1, 29)).getUTCMonth() !== 1) continue;
      if (!emit(at(y, s.month, s.day))) break;
    }
  }
  return out;
}

/**
 * "This and following events": the rule cut to end before the occurrence at `at` (original start), and the rule for
 * a new series starting there. `count` is shared out; exdates and single-occurrence changes go with their side.
 * `before` is undefined when `at` is the first occurrence (then the change covers the whole series).
 */
export function splitRecurrence(e: Expandable & { recurrence: Recurrence }, at: number): { before?: Recurrence; after: Recurrence } {
  const r = e.recurrence;
  const { exdates, overrides, count, until, ...rule } = r;
  const earlier = expandOccurrences({ ...e, recurrence: { ...rule, ...(until === undefined ? {} : { until }), ...(count === undefined ? {} : { count }) } }, e.start, at, MAX_OCCURRENCES).filter((o) => o.start < at).length;
  const side = <T>(list: T[] | undefined, key: (x: T) => number, later: boolean) => (list ?? []).filter((x) => (key(x) >= at) === later);
  const exAfter = side(exdates, (x) => x, true);
  const ovAfter = side(overrides, (o) => o.at, true);
  const after: Recurrence = {
    ...rule,
    ...(until === undefined ? {} : { until }),
    ...(count === undefined ? {} : { count: Math.max(1, count - earlier) }),
    ...(exAfter.length > 0 ? { exdates: exAfter } : {}),
    ...(ovAfter.length > 0 ? { overrides: ovAfter } : {}),
  };
  if (earlier === 0) return { after };
  const exBefore = side(exdates, (x) => x, false);
  const ovBefore = side(overrides, (o) => o.at, false);
  return { before: { ...rule, until: at - 1, ...(exBefore.length > 0 ? { exdates: exBefore } : {}), ...(ovBefore.length > 0 ? { overrides: ovBefore } : {}) }, after };
}

/** The same repeat rule, apart from its per-occurrence parts (exdates, single-occurrence changes)? */
export function sameRule(a: Recurrence, b: Recurrence): boolean {
  return rrule(a) === rrule(b);
}

/** My reminder for one occurrence (original start `at`): its own, else the series'; null = none. */
export function occurrenceReminder(e: Pick<CalendarEvent, "reminderMinutes" | "myOccurrenceReminders">, at: number): number | null {
  const own = e.myOccurrenceReminders?.find((r) => r.at === at);
  return own !== undefined ? own.minutes : (e.reminderMinutes ?? null);
}

// ---------------------------------------------------------------------------------------------------------------
// Reminders (Google Calendar's notifications)

/** Google Calendar's defaults: 10 minutes before; all-day events the day before at 9:00 (900 minutes before midnight). */
export function defaultReminderMinutes(allDay: boolean): number {
  return allDay ? 900 : 10;
}

/** The choices the editor and the event offer (minutes before; all-day ones count back from local midnight). */
export function reminderChoices(allDay: boolean): number[] {
  return allDay ? [0, 900, 2340, 9540] : [0, 5, 10, 15, 30, 60, 120, 1440];
}

/**
 * When a reminder `minutes` before an occurrence starting at `start` fires. Timed events: that many minutes earlier.
 * All-day events count back on the local wall clock from their midnight, so 900 is the day before at 9:00 across DST.
 */
export function reminderAt(start: number, minutes: number, allDay: boolean, timeZone: string): number {
  if (!allDay) return start - minutes * MINUTE;
  const p = localParts(start, timeZone);
  const days = Math.ceil(minutes / 1440);
  const ofDay = days * 1440 - minutes;
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day - days));
  return fromLocal(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), Math.floor(ofDay / 60), ofDay % 60, timeZone);
}

/**
 * A reminder as the picker names it: "No reminder", "At time of event", "10 minutes before", "1 hour before",
 * "1 day before"; all-day ones by day and wall-clock time ("The day before at 09:00", "On the day at 00:00").
 */
export function reminderLabel(minutes: number | null | undefined, allDay: boolean): MessageDescriptor {
  if (minutes === null || minutes === undefined) return msg("calendar.reminder.none");
  if (allDay) {
    const days = Math.ceil(minutes / 1440);
    const ofDay = days * 1440 - minutes;
    const time = `${String(Math.floor(ofDay / 60)).padStart(2, "0")}:${String(ofDay % 60).padStart(2, "0")}`;
    return days === 0 ? msg("calendar.reminder.sameDayAt", { time }) : msg("calendar.reminder.daysBeforeAt", { count: days, time });
  }
  if (minutes === 0) return msg("calendar.reminder.atStart");
  if (minutes % 1440 === 0) return msg("calendar.reminder.days", { count: minutes / 1440 });
  if (minutes % 60 === 0) return msg("calendar.reminder.hours", { count: minutes / 60 });
  return msg("calendar.event.reminderMinutes", { count: minutes });
}

export type DueReminder = {
  /** The occurrence's original start (its id in a series; the event's start for a single event). */
  at: number;
  /** When the occurrence starts (moved occurrences start elsewhere). */
  start: number;
  /** When to remind. */
  fireAt: number;
  minutes: number;
};

/** How far ahead `nextReminder` looks at once, and how many such windows. */
const REMINDER_WINDOW = 120 * DAY;
const REMINDER_WINDOWS = 12;

/**
 * My next reminder for an event firing after `after`: the earliest over its occurrences (moved ones at their new time),
 * each with my reminder for it (`occurrenceReminder`), skipping occurrences I declined. Undefined when none is left.
 */
export function nextReminder(
  e: Expandable &
    Pick<CalendarEvent, "id" | "allDay"> &
    Partial<Pick<CalendarEvent, "cancelled" | "reminderMinutes" | "myOccurrenceReminders" | "myOccurrenceResponses" | "myResponse">>,
  after: number,
): DueReminder | undefined {
  if (e.cancelled === true) return undefined;
  if (e.reminderMinutes === undefined && (e.myOccurrenceReminders ?? []).every((r) => r.minutes === null)) return undefined;
  // A reminder fires at most 28 days (+ a day for all-day wall-clock time) before its occurrence.
  const lead = 40_320 * MINUTE + 2 * DAY;
  const last = seriesEnd(e);
  for (let w = 0; w < REMINDER_WINDOWS; w++) {
    const from = after + w * REMINDER_WINDOW;
    if (last !== undefined && from > last) return undefined;
    const to = from + REMINDER_WINDOW;
    let best: DueReminder | undefined;
    for (const o of occurrencesOf(e, from, to + lead, MAX_OCCURRENCES)) {
      if (o.start <= after) continue;
      const at = o.at ?? o.start;
      if ((o.myResponse ?? e.myResponse) === "declined") continue;
      const minutes = occurrenceReminder(e, at);
      if (minutes === null) continue;
      const fireAt = reminderAt(o.start, minutes, e.allDay, e.timeZone);
      if (fireAt <= after) continue;
      if (best === undefined || fireAt < best.fireAt) best = { at, start: o.start, fireAt, minutes };
    }
    // Anything starting past this window fires after `to`, so a reminder due inside it is the next one.
    if (best !== undefined && best.fireAt <= to) return best;
    if (best !== undefined && w === REMINDER_WINDOWS - 1) return best;
  }
  return undefined;
}

/**
 * An attendee's answer for one occurrence (original start `at`): their answer to it alone, else their "this and
 * following" answer covering it, else their answer to the series.
 */
export function attendeeResponse(a: Pick<Attendee, "response" | "occurrenceResponses">, at: number | undefined): RsvpStatus {
  if (at === undefined) return a.response;
  return occurrenceResponse(a.occurrenceResponses, at) ?? a.response;
}

/** The last instant a recurring event can occur (for range queries), or undefined when it never ends. */
export function seriesEnd(e: Expandable): number | undefined {
  const r = e.recurrence;
  if (r === undefined) return e.end;
  if (r.until !== undefined) return r.until + (e.end - e.start);
  if (r.count !== undefined) {
    const occ = expandOccurrences(e, e.start, Number.MAX_SAFE_INTEGER, r.count);
    return occ[occ.length - 1]?.end ?? e.end;
  }
  return undefined;
}

// ---------------------------------------------------------------------------------------------------------------
// Day layout

export type Placed = { id: string; start: number; end: number; column: number; columns: number };

/**
 * Side-by-side layout of a day's timed events (Google Calendar): events that overlap share the width; each gets the
 * first free column of its overlap cluster, and `columns` is the cluster's width. Input order doesn't matter.
 */
export function layoutDay(items: { id: string; start: number; end: number }[]): Placed[] {
  const sorted = items.toSorted((a, b) => a.start - b.start || b.end - a.end || (a.id < b.id ? -1 : 1));
  const out: Placed[] = [];
  let cluster: Placed[] = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    const width = cluster.reduce((n, p) => Math.max(n, p.column + 1), 0);
    for (const p of cluster) p.columns = width;
    out.push(...cluster);
    cluster = [];
  };
  for (const it of sorted) {
    const end = Math.max(it.end, it.start + 15 * MINUTE); // a zero-length event still takes a sliver
    if (it.start >= clusterEnd) {
      flush();
      clusterEnd = -Infinity;
    }
    let column = 0;
    while (cluster.some((p) => p.column === column && Math.max(p.end, p.start + 15 * MINUTE) > it.start)) column++;
    cluster.push({ id: it.id, start: it.start, end: it.end, column, columns: 1 });
    clusterEnd = Math.max(clusterEnd, end);
  }
  flush();
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// iCalendar (invites to outside guests, their replies)

const pad = (n: number, w = 2) => String(n).padStart(w, "0");

/** 20261023T150000Z */
export function icsUtc(ts: number): string {
  const d = new Date(ts);
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

/** 20261023 (the local date in the event's zone). */
export function icsDate(ts: number, timeZone: string): string {
  const p = localParts(ts, timeZone);
  return `${p.year}${pad(p.month)}${pad(p.day)}`;
}

/** Escapes a TEXT value (RFC 5545 §3.3.11). */
export function icsText(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
}

/** Folds content lines at 75 octets (RFC 5545 §3.1), continuing with a space. */
export function fold(line: string): string {
  const bytes = new TextEncoder().encode(line);
  if (bytes.length <= 75) return line;
  const parts: string[] = [];
  let cur = "";
  let size = 0;
  for (const ch of line) {
    const n = new TextEncoder().encode(ch).length;
    if (size + n > (parts.length === 0 ? 75 : 74)) {
      parts.push(cur);
      cur = "";
      size = 0;
    }
    cur += ch;
    size += n;
  }
  parts.push(cur);
  return parts.join("\r\n ");
}

const DAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

/** RRULE value for a recurrence. */
export function rrule(r: Recurrence): string {
  const parts = [`FREQ=${r.freq.toUpperCase()}`];
  if ((r.interval ?? 1) > 1) parts.push(`INTERVAL=${r.interval}`);
  if (r.freq === "weekly" && r.weekdays !== undefined && r.weekdays.length > 0) parts.push(`BYDAY=${[...r.weekdays].sort((a, b) => a - b).map((d) => DAYS[d]).join(",")}`);
  if (r.until !== undefined) parts.push(`UNTIL=${icsUtc(r.until)}`);
  else if (r.count !== undefined) parts.push(`COUNT=${r.count}`);
  return parts.join(";");
}

export const PARTSTAT: Record<RsvpStatus, string> = { needs_action: "NEEDS-ACTION", accepted: "ACCEPTED", declined: "DECLINED", tentative: "TENTATIVE" };

export type IcsInvite = {
  method: "REQUEST" | "CANCEL";
  /** Globally unique: `<eventId>@<domain>`. */
  uid: string;
  /** Bumped on every change the guests should see. */
  sequence: number;
  stamp: number;
  organizer: { email: string; name?: string | undefined };
  attendees: { email: string; name?: string | undefined; response: RsvpStatus; optional?: boolean | undefined }[];
  event: Pick<CalendarEvent, "title" | "start" | "end" | "allDay" | "timeZone"> & { description?: string | undefined; location?: string | undefined; recurrence?: Recurrence | undefined };
  /** A link to the event in Bundle (added to the description). */
  url?: string | undefined;
  /**
   * Whether guests are asked to answer again (ATTENDEE;RSVP=TRUE, the default). False for an update that keeps their
   * answers (a moved video link: Google Calendar's "Updated invitation" with "Your answer: kept").
   */
  rsvp?: boolean | undefined;
};

/** A VCALENDAR with one VEVENT (METHOD:REQUEST to invite/update, CANCEL to cancel), CRLF line ends, folded. */
export function buildIcs(i: IcsInvite): string {
  return buildIcsLines(i, [...veventLines(i, i.method === "CANCEL"), ...overrideLines(i)]);
}

/**
 * An invite about one occurrence of a series (iTIP with RECURRENCE-ID = its original start `o.at`): REQUEST when it
 * moved or changed on its own, CANCEL when it was cancelled. `o` gives its time and what differs from the series.
 */
export function buildIcsOccurrence(i: IcsInvite, o: OccurrenceOverride): string {
  const e = i.event;
  const one: IcsInvite = {
    ...i,
    event: { title: o.title ?? e.title, start: o.start, end: o.end, allDay: e.allDay, timeZone: e.timeZone, description: o.description ?? e.description, location: o.location ?? e.location },
  };
  const lines = veventLines(one, i.method === "CANCEL");
  const uid = lines.findIndex((l) => l.startsWith("UID:"));
  lines.splice(uid + 1, 0, e.allDay ? `RECURRENCE-ID;VALUE=DATE:${icsDate(o.at, e.timeZone)}` : `RECURRENCE-ID:${icsUtc(o.at)}`);
  return buildIcsLines(i, lines);
}

/** The original start a REPLY's RECURRENCE-ID names (a guest answering one occurrence), or undefined for the series. */
export function icsReplyRecurrenceId(text: string, timeZone: string): number | undefined {
  const rid = icsLines(text).find(([k]) => k.split(";")[0]!.toUpperCase() === "RECURRENCE-ID");
  return rid === undefined ? undefined : icsTime(rid[0], rid[1], timeZone)?.ts;
}

/** A REPLY answering its RECURRENCE-ID and every later occurrence (`RANGE=THISANDFUTURE`: "this and following"). */
export function icsReplyThisAndFuture(text: string): boolean {
  const rid = icsLines(text).find(([k]) => k.split(";")[0]!.toUpperCase() === "RECURRENCE-ID");
  return rid !== undefined && /;RANGE=THISANDFUTURE(;|$)/i.test(rid[0]);
}

function buildIcsLines(i: IcsInvite, vevents: string[]): string {
  const lines = ["BEGIN:VCALENDAR", "PRODID:-//Bundle//Calendar//EN", "VERSION:2.0", "CALSCALE:GREGORIAN", `METHOD:${i.method}`, ...vevents];
  return `${[...lines, "END:VCALENDAR"].map(fold).join("\r\n")}\r\n`;
}

/** One event in a whole calendar (subscription feed, CalDAV): no organizer or attendees, so apps show it as mine. */
export type IcsFeedEvent = Pick<IcsInvite, "uid" | "sequence" | "stamp" | "event" | "url"> & { cancelled?: boolean | undefined };

/**
 * A whole calendar as one VCALENDAR (no METHOD), for subscriptions ("Subscribe to calendar" / "From URL") and CalDAV
 * objects: the calendar's name, a refresh hint, and one VEVENT per event.
 */
export function buildIcsFeed(name: string, events: IcsFeedEvent[]): string {
  const lines = [
    "BEGIN:VCALENDAR",
    "PRODID:-//Bundle//Calendar//EN",
    "VERSION:2.0",
    "CALSCALE:GREGORIAN",
    `X-WR-CALNAME:${icsText(name)}`,
    "REFRESH-INTERVAL;VALUE=DURATION:PT15M",
    "X-PUBLISHED-TTL:PT15M",
    ...events.flatMap((e) => [...veventLines(e, e.cancelled === true), ...overrideLines(e)]),
  ];
  return `${[...lines, "END:VCALENDAR"].map(fold).join("\r\n")}\r\n`;
}

/** The series' single-occurrence changes as VEVENTs with RECURRENCE-ID (same UID), after the main one. */
function overrideLines(i: IcsFeedEvent): string[] {
  const e = i.event;
  return (e.recurrence?.overrides ?? []).flatMap((o) => {
    const description = o.description ?? e.description;
    const location = o.location ?? e.location;
    return [
      "BEGIN:VEVENT",
      `UID:${i.uid}`,
      e.allDay ? `RECURRENCE-ID;VALUE=DATE:${icsDate(o.at, e.timeZone)}` : `RECURRENCE-ID:${icsUtc(o.at)}`,
      `SEQUENCE:${i.sequence}`,
      `DTSTAMP:${icsUtc(i.stamp)}`,
      ...(e.allDay ? [`DTSTART;VALUE=DATE:${icsDate(o.start, e.timeZone)}`, `DTEND;VALUE=DATE:${icsDate(o.end, e.timeZone)}`] : [`DTSTART:${icsUtc(o.start)}`, `DTEND:${icsUtc(o.end)}`]),
      `SUMMARY:${icsText(o.title ?? e.title)}`,
      ...(location === undefined || location === "" ? [] : [`LOCATION:${icsText(location)}`]),
      ...(description === undefined || description === "" ? [] : [`DESCRIPTION:${icsText(description)}`]),
      "STATUS:CONFIRMED",
      "END:VEVENT",
    ];
  });
}

function veventLines(i: IcsFeedEvent & Partial<Pick<IcsInvite, "organizer" | "attendees" | "rsvp">>, cancelled: boolean): string[] {
  const e = i.event;
  const cn = (name: string | undefined) => (name === undefined || name === "" ? "" : `;CN="${name.replace(/"/g, "'")}"`);
  return [
    "BEGIN:VEVENT",
    `UID:${i.uid}`,
    `SEQUENCE:${i.sequence}`,
    `DTSTAMP:${icsUtc(i.stamp)}`,
    ...(e.allDay
      ? [`DTSTART;VALUE=DATE:${icsDate(e.start, e.timeZone)}`, `DTEND;VALUE=DATE:${icsDate(e.end, e.timeZone)}`]
      : [`DTSTART:${icsUtc(e.start)}`, `DTEND:${icsUtc(e.end)}`]),
    ...(e.recurrence === undefined ? [] : [`RRULE:${rrule(e.recurrence)}`]),
    ...(e.recurrence?.exdates ?? []).map((x) => (e.allDay ? `EXDATE;VALUE=DATE:${icsDate(x, e.timeZone)}` : `EXDATE:${icsUtc(x)}`)),
    `SUMMARY:${icsText(e.title)}`,
    ...(e.location === undefined || e.location === "" ? [] : [`LOCATION:${icsText(e.location)}`]),
    ...(e.description === undefined && i.url === undefined ? [] : [`DESCRIPTION:${icsText([e.description ?? "", i.url ?? ""].filter((x) => x !== "").join("\n\n"))}`]),
    ...(i.url === undefined ? [] : [`URL:${i.url}`]),
    ...(i.organizer === undefined ? [] : [`ORGANIZER${cn(i.organizer.name)}:mailto:${i.organizer.email}`]),
    ...(i.attendees ?? []).map(
      (a) => `ATTENDEE${cn(a.name)};ROLE=${a.optional === true ? "OPT-PARTICIPANT" : "REQ-PARTICIPANT"};PARTSTAT=${PARTSTAT[a.response]};RSVP=${i.rsvp === false ? "FALSE" : "TRUE"}:mailto:${a.email}`,
    ),
    `STATUS:${cancelled ? "CANCELLED" : "CONFIRMED"}`,
    "END:VEVENT",
  ];
}

/** Unfolds and splits an iCalendar text into [name+params, value] lines. */
export function icsLines(text: string): [string, string][] {
  const unfolded = text.replace(/\r?\n[ \t]/g, "");
  const out: [string, string][] = [];
  for (const line of unfolded.split(/\r?\n/)) {
    const i = line.indexOf(":");
    if (i > 0) out.push([line.slice(0, i), line.slice(i + 1)]);
  }
  return out;
}

/**
 * A guest's answer to an invite (METHOD:REPLY, as Gmail/Outlook send it): the event uid, who answered and how.
 * Undefined when the text isn't a reply.
 */
export function parseIcsReply(text: string): { uid: string; email: string; response: RsvpStatus } | undefined {
  const lines = icsLines(text);
  if (!lines.some(([k, v]) => k.toUpperCase() === "METHOD" && v.trim().toUpperCase() === "REPLY")) return undefined;
  const uid = lines.find(([k]) => k.toUpperCase() === "UID")?.[1]?.trim();
  const att = lines.find(([k]) => k.toUpperCase().startsWith("ATTENDEE"));
  if (uid === undefined || att === undefined) return undefined;
  const email = att[1].replace(/^mailto:/i, "").trim().toLowerCase();
  const ps = /PARTSTAT=([A-Z-]+)/i.exec(att[0])?.[1]?.toUpperCase();
  const response: RsvpStatus | undefined = ps === "ACCEPTED" ? "accepted" : ps === "DECLINED" ? "declined" : ps === "TENTATIVE" ? "tentative" : undefined;
  if (response === undefined || email === "") return undefined;
  return { uid, email, response };
}

/** An event written by a calendar app (CalDAV PUT): the fields Bundle keeps. */
export type ParsedIcsEvent = {
  uid: string;
  title: string;
  description?: string;
  location?: string;
  start: number;
  end: number;
  allDay: boolean;
  timeZone: string;
  recurrence?: Recurrence;
};

/** Undoes `icsText`. */
export function icsUnescape(s: string): string {
  return s.replace(/\\([nN]|[\\;,])/g, (_m: string, c: string) => (c === "n" || c === "N" ? "\n" : c));
}

/** A DTSTART/DTEND/EXDATE value with its params: UTC (Z), floating or TZID local time, or a DATE. */
export function icsTime(params: string, value: string, fallbackZone: string): { ts: number; allDay: boolean; zone: string } | undefined {
  const zone = /;TZID=("?)([^;:"]+)\1/i.exec(params)?.[2] ?? fallbackZone;
  const d = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})(Z?))?$/.exec(value.trim());
  if (d === null) return undefined;
  const [y, mo, da] = [Number(d[1]), Number(d[2]), Number(d[3])];
  if (d[4] === undefined) return { ts: fromLocal(y, mo, da, 0, 0, zone), allDay: true, zone };
  if (d[7] === "Z") return { ts: Date.UTC(y, mo - 1, da, Number(d[4]), Number(d[5]), Number(d[6])), allDay: false, zone };
  return { ts: fromLocal(y, mo, da, Number(d[4]), Number(d[5]), zone) + Number(d[6]) * 1000, allDay: false, zone };
}

/** ISO 8601 duration (DURATION:PT1H30M, P1D) in ms. */
export function icsDuration(v: string): number | undefined {
  const m = /^([+-])?P(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(v.trim());
  if (m === null) return undefined;
  const ms = ((Number(m[2] ?? 0) * 7 + Number(m[3] ?? 0)) * 24 * 3600 + Number(m[4] ?? 0) * 3600 + Number(m[5] ?? 0) * 60 + Number(m[6] ?? 0)) * 1000;
  return m[1] === "-" ? -ms : ms;
}

/** RRULE → Recurrence (FREQ, INTERVAL, BYDAY for weekly, COUNT, UNTIL); undefined for rules Bundle can't keep. */
export function parseRrule(value: string, timeZone: string): Recurrence | undefined {
  const p = new Map(value.split(";").map((kv) => kv.split("=") as [string, string]).map(([k, v]) => [k.toUpperCase(), v ?? ""]));
  const freqs: Record<string, Recurrence["freq"]> = { DAILY: "daily", WEEKLY: "weekly", MONTHLY: "monthly", YEARLY: "yearly" };
  const freq = freqs[(p.get("FREQ") ?? "").toUpperCase()];
  if (freq === undefined) return undefined;
  const r: Recurrence = { freq, interval: Math.min(99, Math.max(1, Number(p.get("INTERVAL") ?? "1") || 1)) };
  const byday = p.get("BYDAY");
  if (freq === "weekly" && byday !== undefined) {
    const days = byday.split(",").map((d) => DAYS.indexOf(d.replace(/^[+-]?\d+/, "").toUpperCase())).filter((d) => d >= 0);
    if (days.length > 0) r.weekdays = [...new Set(days)].sort((a, b) => a - b);
  }
  const until = p.get("UNTIL");
  if (until !== undefined) {
    const t = icsTime("", until, timeZone);
    if (t !== undefined) r.until = t.allDay ? t.ts + DAY - 1 : t.ts;
  } else if (p.get("COUNT") !== undefined) r.count = Math.min(1000, Math.max(1, Number(p.get("COUNT")) || 1));
  return r;
}

/**
 * The main VEVENT of an iCalendar object (not an override with RECURRENCE-ID), as a calendar app PUTs it over CalDAV.
 * Times with a TZID are converted with that IANA zone (Apple and Google use IANA names); floating times use
 * `defaultZone`. Undefined when there's no usable event.
 */
export function parseIcsEvent(text: string, defaultZone: string): ParsedIcsEvent | undefined {
  const lines = icsLines(text);
  let inEvent = false;
  let depth = 0;
  let cur: [string, string][] = [];
  const events: [string, string][][] = [];
  for (const [k, v] of lines) {
    const key = k.toUpperCase();
    if (key === "BEGIN" && v.trim().toUpperCase() === "VEVENT") {
      inEvent = true;
      depth = 0;
      cur = [];
    } else if (inEvent && key === "BEGIN") depth++;
    else if (inEvent && key === "END" && v.trim().toUpperCase() === "VEVENT") {
      inEvent = false;
      events.push(cur);
    } else if (inEvent && key === "END") depth--;
    else if (inEvent && depth === 0) cur.push([k, v]);
  }
  const ev = events.find((e) => !e.some(([k]) => /^RECURRENCE-ID/i.test(k)));
  if (ev === undefined) return undefined;
  const get = (name: string) => ev.find(([k]) => k.split(";")[0]!.toUpperCase() === name);
  const uid = get("UID")?.[1]?.trim();
  const ds = get("DTSTART");
  if (uid === undefined || uid === "" || ds === undefined) return undefined;
  const start = icsTime(ds[0], ds[1], defaultZone);
  if (start === undefined) return undefined;
  const de = get("DTEND");
  const dur = get("DURATION");
  const endT = de !== undefined ? icsTime(de[0], de[1], start.zone)?.ts : dur !== undefined ? (icsDuration(dur[1]) ?? 0) + start.ts : start.ts + (start.allDay ? DAY : HOUR);
  const out: ParsedIcsEvent = { uid, title: icsUnescape(get("SUMMARY")?.[1] ?? "").trim() || "(No title)", start: start.ts, end: endT ?? start.ts + HOUR, allDay: start.allDay, timeZone: start.zone };
  const desc = get("DESCRIPTION")?.[1];
  if (desc !== undefined && desc !== "") out.description = icsUnescape(desc);
  const loc = get("LOCATION")?.[1];
  if (loc !== undefined && loc !== "") out.location = icsUnescape(loc);
  const rr = get("RRULE");
  const rec = rr === undefined ? undefined : parseRrule(rr[1], start.zone);
  if (rec !== undefined) {
    const ex = ev.filter(([k]) => k.split(";")[0]!.toUpperCase() === "EXDATE").flatMap(([k, v]) => v.split(",").map((x) => icsTime(k, x, start.zone)?.ts)).filter((x): x is number => x !== undefined);
    if (ex.length > 0) rec.exdates = ex.slice(0, 500);
    // Occurrences changed on their own: the other VEVENTs with this UID and a RECURRENCE-ID.
    const overrides: OccurrenceOverride[] = [];
    for (const o of events) {
      const field = (name: string) => o.find(([k]) => k.split(";")[0]!.toUpperCase() === name);
      const rid = field("RECURRENCE-ID");
      const os = field("DTSTART");
      if (rid === undefined || os === undefined || field("UID")?.[1]?.trim() !== uid) continue;
      const at = icsTime(rid[0], rid[1], start.zone)?.ts;
      const s = icsTime(os[0], os[1], start.zone)?.ts;
      if (at === undefined || s === undefined) continue;
      const oe = field("DTEND");
      const od = field("DURATION");
      const e2 = oe !== undefined ? icsTime(oe[0], oe[1], start.zone)?.ts : od !== undefined ? s + (icsDuration(od[1]) ?? 0) : s + (out.end - out.start);
      const ov: OccurrenceOverride = { at, start: s, end: e2 !== undefined && e2 > s ? e2 : s + (out.end - out.start) };
      const title = icsUnescape(field("SUMMARY")?.[1] ?? "").trim();
      if (title !== "" && title !== out.title) ov.title = title;
      const loc = field("LOCATION")?.[1];
      if (loc !== undefined && icsUnescape(loc) !== (out.location ?? "")) ov.location = icsUnescape(loc);
      const d = field("DESCRIPTION")?.[1];
      if (d !== undefined && icsUnescape(d) !== (out.description ?? "")) ov.description = icsUnescape(d);
      if (/^CANCELLED$/i.test(field("STATUS")?.[1]?.trim() ?? "")) rec.exdates = [...(rec.exdates ?? []), at];
      else overrides.push(ov);
    }
    if (overrides.length > 0) rec.overrides = overrides.sort((a, b) => a.at - b.at).slice(0, 200);
    out.recurrence = rec;
  }
  return out;
}

/** Problems with an event's times (empty = fine). */
export function eventProblems(e: { start: number; end: number; allDay: boolean; title: string }): ("noTitle" | "endBeforeStart" | "tooLong")[] {
  const out: ("noTitle" | "endBeforeStart" | "tooLong")[] = [];
  if (e.title.trim() === "") out.push("noTitle");
  if (e.end <= e.start) out.push("endBeforeStart");
  if (e.end - e.start > 366 * DAY) out.push("tooLong");
  return out;
}

/** Bundle's own hosts: a meeting link (`<origin>/m/abc-defg-hij`) on one of these is a Bundle meeting. */
const BUNDLE_HOSTS = /^(?:[\w-]+\.)*bundle\.direct$|^localhost$|^127\.0\.0\.1$/;

/**
 * The meeting code of a Bundle meeting link (`https://app.bundle.direct/m/abc-defg-hij`, huddles/meetings.ts
 * `meetingUrl`), or undefined when the URL isn't one.
 */
export function bundleMeetingCode(url: string): string | undefined {
  try {
    const u = new URL(url);
    if (!BUNDLE_HOSTS.test(u.hostname)) return undefined;
    return /^\/m\/([a-z]{3}-[a-z]{4}-[a-z]{3})\/?$/.exec(u.pathname)?.[1];
  } catch {
    return undefined;
  }
}

/**
 * An event's video call link: "bundle" (a Bundle meeting: "Join Bundle meeting"), "meet" (Google Meet: "Join with Google Meet"), "video" (any other web link: "Join video
 * call"), or undefined when it isn't an http(s) link (invites come from outside: nothing else is opened).
 */
export function conferenceKind(url: string): "meet" | "bundle" | "video" | undefined {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:" && u.protocol !== "http:") return undefined;
    if (bundleMeetingCode(url) !== undefined) return "bundle";
    return u.hostname === "meet.google.com" ? "meet" : "video";
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Editor and event page (Google Calendar's repeat menu, custom recurrence, time zone row, guest list)

/** The repeat menu (Google Calendar's): its presets, and "custom" for any other rule (every N, ends on / after). */
export type RepeatPreset = "none" | "daily" | "weekly" | "weekdays" | "monthly" | "yearly" | "custom";

const WORKWEEK = [1, 2, 3, 4, 5];
const sameDays = (a: readonly number[] | undefined, b: readonly number[]) => a !== undefined && a.length === b.length && b.every((d) => a.includes(d));

/** Which repeat menu entry a rule is: a preset when it is one exactly (every 1, never ends), else "custom". */
export function repeatPreset(r: Recurrence | undefined, start: number, timeZone: string): RepeatPreset {
  if (r === undefined) return "none";
  if ((r.interval ?? 1) !== 1 || r.until !== undefined || r.count !== undefined) return "custom";
  if (r.freq !== "weekly") return r.weekdays === undefined || r.weekdays.length === 0 ? r.freq : "custom";
  if (sameDays(r.weekdays, WORKWEEK)) return "weekdays";
  if (r.weekdays === undefined || r.weekdays.length === 0 || sameDays(r.weekdays, [localParts(start, timeZone).weekday])) return "weekly";
  return "custom";
}

/** The rule for a preset (undefined = does not repeat), keeping the occurrences already removed from the series. */
export function presetRecurrence(p: Exclude<RepeatPreset, "custom">, start: number, timeZone: string, old?: Recurrence): Recurrence | undefined {
  if (p === "none") return undefined;
  const keep: Pick<Recurrence, "exdates"> = old?.exdates === undefined ? {} : { exdates: old.exdates };
  if (p === "weekdays") return { freq: "weekly", interval: 1, weekdays: [...WORKWEEK], ...keep };
  if (p === "weekly") return { freq: "weekly", interval: 1, weekdays: [localParts(start, timeZone).weekday], ...keep };
  return { freq: p, interval: 1, ...keep };
}

/**
 * Google Calendar's "Custom recurrence" dialog: repeat every `interval` days / weeks (on `weekdays`) / months / years;
 * ends never, on a date (`until`: any instant of that local day, the last day it can occur) or after `count` times.
 */
export type CustomRepeat = {
  freq: Recurrence["freq"];
  interval: number;
  weekdays: number[];
  ends: "never" | "on" | "after";
  until: number;
  count: number;
};

/** The custom dialog's fields for a rule (or for an event that doesn't repeat yet: weekly on its day, Google's default). */
export function customRepeatOf(r: Recurrence | undefined, start: number, timeZone: string): CustomRepeat {
  const day = localParts(start, timeZone).weekday;
  // Google offers "ends on" a month after the start until one is picked.
  const monthLater = startOfDay(addDays(start, 30, timeZone), timeZone);
  if (r === undefined) return { freq: "weekly", interval: 1, weekdays: [day], ends: "never", until: monthLater, count: 13 };
  return {
    freq: r.freq,
    interval: Math.max(1, r.interval ?? 1),
    weekdays: r.freq === "weekly" ? (r.weekdays !== undefined && r.weekdays.length > 0 ? [...r.weekdays].sort((a, b) => a - b) : [day]) : [],
    ends: r.until !== undefined ? "on" : r.count !== undefined ? "after" : "never",
    until: r.until !== undefined ? startOfDay(r.until, timeZone) : monthLater,
    count: r.count ?? 13,
  };
}

/** The rule the custom dialog gives (`until` becomes the end of its local day: inclusive, like RRULE UNTIL). */
export function customRecurrence(c: CustomRepeat, start: number, timeZone: string, old?: Recurrence): Recurrence {
  const r: Recurrence = { freq: c.freq, interval: Math.min(99, Math.max(1, Math.round(c.interval))) };
  if (c.freq === "weekly") r.weekdays = c.weekdays.length > 0 ? [...new Set(c.weekdays)].sort((a, b) => a - b) : [localParts(start, timeZone).weekday];
  if (c.ends === "on") r.until = addDays(startOfDay(Math.max(c.until, start), timeZone), 1, timeZone) - 1;
  if (c.ends === "after") r.count = Math.min(1000, Math.max(1, Math.round(c.count)));
  if (old?.exdates !== undefined) r.exdates = old.exdates;
  return r;
}

/**
 * How a rule reads ("Every 2 weeks on Mon, Wed", then "until Dec 1, 2026" or "13 times"): the rule's message, and the
 * ending's when it ends. `days` is the platform's weekday names joined ("Mon, Wed"), `until` its formatted last day.
 */
export function repeatLabel(r: Recurrence, days: string, until: string): MessageDescriptor[] {
  const count = Math.max(1, r.interval ?? 1);
  const rule =
    r.freq === "weekly" && r.weekdays !== undefined && r.weekdays.length > 0
      ? sameDays(r.weekdays, WORKWEEK) && count === 1
        ? msg("calendar.repeat.weekdays")
        : msg("calendar.repeat.every.weeklyOn", { count, days })
      : msg(`calendar.repeat.every.${r.freq}`, { count });
  if (r.until !== undefined) return [rule, msg("calendar.repeat.until", { date: until })];
  if (r.count !== undefined) return [rule, msg("calendar.repeat.times", { count: r.count })];
  return [rule];
}

/** The same wall-clock time in another zone (the editor's time zone row: 12:00 stays 12:00, now in that zone). */
export function inZone(ts: number, from: string, to: string): number {
  const p = localParts(ts, from);
  return fromLocal(p.year, p.month, p.day, p.hour, p.minute, to);
}

/** A zone's offset from UTC at an instant, in minutes (São Paulo: -180). */
export function zoneOffset(ts: number, timeZone: string): number {
  const p = localParts(ts, timeZone);
  return Math.round((Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(ts / 1000) * 1000) / 60_000);
}

/** "GMT-03:00", the time zone row's offset (Google Calendar's "(GMT-03:00) São Paulo"). */
export function gmtLabel(ts: number, timeZone: string): string {
  const o = zoneOffset(ts, timeZone);
  const a = Math.abs(o);
  return `GMT${o < 0 ? "-" : "+"}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}

/** Google's answer order everywhere: Yes, No, Maybe. */
export const RSVP_CHOICES: readonly Exclude<RsvpStatus, "needs_action">[] = ["accepted", "declined", "tentative"];

/** An answer as the guest list labels it ("Yes", "No", "Maybe", "Awaiting"). */
export function rsvpLabel(r: RsvpStatus): MessageDescriptor {
  return msg(r === "accepted" ? "calendar.rsvp.accepted" : r === "declined" ? "calendar.rsvp.declined" : r === "tentative" ? "calendar.rsvp.tentative" : "calendar.rsvp.needsAction");
}

/** The colour of an answer's label: Yes green, No red, Maybe accent, Awaiting grey. */
export function rsvpTone(r: RsvpStatus): "success" | "danger" | "accent" | "neutral" {
  return r === "accepted" ? "success" : r === "declined" ? "danger" : r === "tentative" ? "accent" : "neutral";
}

/** One line of the event page's guest list. Members carry `userId` (the app shows their name), guests `email`. */
export type GuestRow = {
  key: string;
  userId?: string;
  email?: string;
  name?: string;
  organizer: boolean;
  me: boolean;
  optional: boolean;
  response: RsvpStatus;
};

/** Full members' addresses (lowercase) → member id, for `mergeAttendees`. Outside guests and agents aren't attendees by id. */
export function memberEmailIndex(members: readonly Pick<Member, "userId" | "kind" | "email" | "access" | "deactivated">[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of members) if (m.email !== undefined && m.kind === "human" && m.access === undefined && !m.deactivated) out[m.email.toLowerCase()] = m.userId;
  return out;
}

/**
 * One attendee per person. A member can be listed twice: by id (they RSVP in the app, their own calendar syncs) and by
 * an address that is theirs, in any case or alias (`memberEmails`, lowercase address → member id). Their rows become
 * one, in the first one's place: an explicit Yes, No or Maybe beats Awaiting (the member's own row first when both
 * have answered), and answers to single occurrences carry over. Optional when any row says so, since a member's own
 * row doesn't know. `link`: an address that's a member's, listed only by address, also becomes that member (false for
 * a member's own copy of an outside event, where other people stay addresses: an id would put my copy on their calendar).
 */
export function mergeAttendees(attendees: readonly Attendee[], memberEmails: Readonly<Record<string, string>> = {}, link = true): Attendee[] {
  const people = new Map<string, Attendee[]>();
  attendees.forEach((a, i) => {
    const id = a.userId ?? (a.email === undefined ? undefined : memberEmails[a.email.toLowerCase()]);
    const key = id !== undefined ? `u:${id}` : a.email !== undefined ? `e:${a.email.toLowerCase()}` : `#${i}`;
    const rows = people.get(key);
    if (rows === undefined) people.set(key, [a]);
    else rows.push(a);
  });
  const out: Attendee[] = [];
  for (const [key, rows] of people) {
    const own = rows.find((a) => a.userId !== undefined);
    const id = own?.userId ?? (key.startsWith("u:") && link ? key.slice(2) : undefined);
    const first = rows[0]!;
    if (rows.length === 1 && (id === undefined || first.userId !== undefined)) {
      out.push(first);
      continue;
    }
    const ordered = own === undefined ? rows : [own, ...rows.filter((a) => a !== own)];
    const merged: Attendee = { response: ordered.find((a) => a.response !== "needs_action")?.response ?? "needs_action" };
    if (id !== undefined) merged.userId = id;
    else if (first.email !== undefined) merged.email = first.email;
    const name = ordered.find((a) => a.name !== undefined)?.name;
    if (name !== undefined) merged.name = name;
    if (rows.some((a) => a.optional === true)) merged.optional = true;
    const answers = new Map<string, NonNullable<Attendee["occurrenceResponses"]>[number]>();
    for (const a of ordered) {
      for (const r of a.occurrenceResponses ?? []) {
        const k = `${r.at}:${r.following === true}`;
        if (!answers.has(k)) answers.set(k, r);
      }
    }
    if (answers.size > 0) merged.occurrenceResponses = [...answers.values()].sort((x, y) => x.at - y.at).slice(-500);
    out.push(merged);
  }
  return out;
}

/**
 * The event page's guests, Google Calendar's way: the organizer first (counted, answered Yes), then me, then everyone
 * else as invited; each with their answer to the occurrence `at` when they gave one on its own. A person shows once
 * (`mergeAttendees`, by the member addresses the app knows), and an outside organizer listed among the attendees shows
 * as the organizer. The list's length is the "N guests" count.
 */
export function guestList(e: Pick<CalendarEvent, "organizerId" | "externalOrganizer" | "attendees">, at: number | undefined, meId: string, memberEmails: Readonly<Record<string, string>> = {}): GuestRow[] {
  const outside = e.externalOrganizer?.email.toLowerCase();
  const outsideMember = outside === undefined ? undefined : memberEmails[outside];
  const organizer: GuestRow =
    e.externalOrganizer === undefined
      ? { key: `u:${e.organizerId}`, userId: e.organizerId, organizer: true, me: e.organizerId === meId, optional: false, response: "accepted" }
      : { key: `e:${outside}`, email: e.externalOrganizer.email, ...(e.externalOrganizer.name === undefined ? {} : { name: e.externalOrganizer.name }), organizer: true, me: false, optional: false, response: "accepted" };
  const rows: GuestRow[] = [];
  for (const a of mergeAttendees(e.attendees, memberEmails, e.externalOrganizer === undefined)) {
    if (outside !== undefined && a.email?.toLowerCase() === outside) continue;
    if (outsideMember !== undefined && (a.userId ?? memberEmails[a.email?.toLowerCase() ?? ""]) === outsideMember) continue;
    if (e.externalOrganizer === undefined && a.userId === e.organizerId) continue;
    const row: GuestRow = {
      key: a.userId === undefined ? `e:${a.email?.toLowerCase() ?? rows.length}` : `u:${a.userId}`,
      organizer: false,
      me: a.userId === meId,
      optional: a.optional === true,
      response: attendeeResponse(a, at),
    };
    if (a.userId !== undefined) row.userId = a.userId;
    if (a.email !== undefined) row.email = a.email;
    if (a.name !== undefined) row.name = a.name;
    rows.push(row);
  }
  return [organizer, ...rows.filter((r) => r.me), ...rows.filter((r) => !r.me)];
}

/** An occurrence that has ended (drawn faded in the grid, like Google Calendar; it can still be answered). */
export function isPast(end: number, now: number): boolean {
  return end <= now;
}
